import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import React, { type ErrorInfo } from 'react';

import Button from '@/components/Button';
import { BrandSymbol } from '@/components/Loaders';
import { useTranslate } from '@/state/i18n';
import { gray, surfaceTokens, typography } from '@/theme/themePrimitives';

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error;
  info: ErrorInfo;
}

/**
 * There is currently no way to write an error boundary as a function component.
 * However, you don’t have to write the error boundary class yourself.
 * For example, you can use react-error-boundary instead.
 * https://react.dev/reference/react/Component#catching-rendering-errors-with-an-error-boundary
 */
class ErrorBoundary extends React.Component<{ children: React.ReactNode }, ErrorBoundaryState> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = {
      hasError: false,
      error: { name: '', message: '', stack: '' },
      info: { componentStack: '' },
    };
  }

  static getDerivedStateFromError() {
    // Update state so the next render will show the fallback UI.
    return { hasError: true };
  }

  componentDidCatch = (error: Error, info: ErrorInfo) => {
    console.error(`From Boundary. [Error: ${error}], [Info: ${info}]`);

    // Sentry is on hold in the partner portal (docs/init/06-implementacao.md, Em espera).
    this.setState({ error, info });
  };

  render() {
    if (!this.state.hasError) {
      // If there is no error, render the children components
      return this.props.children;
    }

    return <ErrorBoundaryFallback />;
  }
}

/** Signal board 17 "Falha de carga": the boot loader's symbol, faded, and a way back in. */
export function ErrorBoundaryFallback() {
  const t = useTranslate('components.ErrorBoundary');

  return (
    <Stack role="alert" sx={sx.root}>
      <BrandSymbol size={47} color={gray[500]} sx={sx.symbol} />
      <Stack alignItems="center" gap={0.75}>
        <Typography component="h1" sx={sx.title}>
          {t('title')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('description')}
        </Typography>
      </Stack>
      <Stack alignItems="center" gap={1}>
        <Button variant="outlined" size="small" onClick={() => window.location.reload()}>
          {t('reload')}
        </Button>
        <Button
          variant="text"
          size="small"
          onClick={() => {
            window.location.replace('/');
          }}
        >
          {t('backHome')}
        </Button>
      </Stack>
    </Stack>
  );
}

const sx = {
  root: {
    width: '100%',
    minHeight: '100dvh',
    px: 2,
    pb: '8vh',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 2.5,
    textAlign: 'center',
    backgroundColor: surfaceTokens.page,
  },
  symbol: { opacity: 0.3 },
  title: {
    fontFamily: typography.h1.fontFamily,
    fontWeight: 600,
    fontSize: 15,
    color: surfaceTokens.dark,
  },
};

export default ErrorBoundary;
